import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, {
  ScreenContent,
} from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

export default function AdminUpdateUserInfo() {
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const [userInfo, setUserInfo] = useState({
    userno: "",
    fullname: "",
    user_tax_id: "",
    email: "",
    reserved_notes: "",
    role: "",
  });

  useEffect(() => {
    axios.defaults.headers.common.Authorization =
      `Bearer ${localStorage.getItem("token")}`;

    fetchUsers();
  }, []);

  const clearUserInfo = () => {
    setUserInfo({
      userno: "",
      fullname: "",
      user_tax_id: "",
      email: "",
      reserved_notes: "",
      role: "",
    });
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/users`
      );

      const fetchedUsers = Array.isArray(response.data)
        ? response.data
        : [];

      setUsers(fetchedUsers);

      return fetchedUsers;
    } catch (error) {
      console.error("❌ Failed to fetch users", error);

      setUsers([]);

      setMessage(
        "❌ " +
          (
            error.response?.data?.detail ||
            t(
              "admin_update_user_info.messages.fetch_failed",
              "Kullanıcılar alınamadı."
            )
          )
      );

      return [];
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadUserIntoForm = (user) => {
    if (!user) {
      clearUserInfo();
      return;
    }

    setUserInfo({
      userno: user.userno || "",
      fullname: user.fullname || "",
      user_tax_id: user.user_tax_id || "",
      email: user.email || "",
      reserved_notes: user.reserved_notes || "",
      role:
        typeof user.role === "string"
          ? user.role
          : user.role?.value || "",
    });
  };

  const submit = async () => {
    if (!selectedUserId) {
      setMessage(
        "❌ " +
          t(
            "admin_update_user_info.messages.missing_user",
            "Lütfen kullanıcı seçin."
          )
      );

      return;
    }

    setSubmitting(true);
    setMessage("");

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/update-user-info`,
        {
          user_id: selectedUserId,
          userno: userInfo.userno,
          fullname: userInfo.fullname,
          user_tax_id: userInfo.user_tax_id,
          email: userInfo.email,
          reserved_notes: userInfo.reserved_notes,
          role: userInfo.role,
        }
      );

      /*
       * Reload /admin/users after saving so the form is populated
       * from the actual committed database state.
       */
      const refreshedUsers = await fetchUsers();

      const refreshedUser = refreshedUsers.find(
        (user) => user.user_id === selectedUserId
      );

      if (refreshedUser) {
        loadUserIntoForm(refreshedUser);
      }

      setMessage(
        "✅ " +
          t(
            "admin_update_user_info.messages.success",
            "Kullanıcı bilgileri güncellendi."
          )
      );
    } catch (error) {
      console.error(
        "❌ Failed to update user information",
        error
      );

      setMessage(
        "❌ " +
          (
            error.response?.data?.detail ||
            t(
              "admin_update_user_info.messages.failure",
              "Kullanıcı bilgileri güncellenemedi."
            )
          )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const options = users.map((user) => ({
    value: user.user_id,
    label:
      `${user.fullname || "-"} | ` +
      `${user.userno || "-"} | ` +
      `${
        typeof user.role === "string"
          ? user.role
          : user.role?.value || "-"
      }`,
    user,
  }));

  const selectedUserOption =
    options.find(
      (option) => option.value === selectedUserId
    ) || null;

  return (
    <StandardScreen
      background="default"
      topNav={
        <TopNav
          title={t(
            "admin_update_user_info.title"
          )}
          mode="main"
        />
      }
    >
      <ScreenContent>
        <div
          style={{
            maxWidth: 720,
            width: "100%",
            margin: "0 auto",
          }}
        >
          <Stack>
            <h1
              style={{
                margin: 0,
                fontSize: "1.5rem",
                fontWeight: 700,
              }}
            >
              {t(
                "admin_update_user_info.title"
              )}
            </h1>

            <Card>
              <Stack>
                <FormField
                  label={t(
                    "admin_update_user_info.fields.select_user.label"
                  )}
                >
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    isLoading={loadingUsers}
                    isDisabled={submitting}
                    options={options}
                    value={selectedUserOption}
                    onChange={(option) => {
                      const user =
                        option?.user || null;

                      setSelectedUserId(
                        user?.user_id || null
                      );

                      loadUserIntoForm(user);

                      setMessage("");
                    }}
                    placeholder={t(
                      "admin_update_user_info.fields.select_user.placeholder"
                    )}
                  />
                </FormField>

                {selectedUserId && (
                  <>
                    {[
                      "userno",
                      "fullname",
                      "user_tax_id",
                      "email",
                    ].map((field) => (
                      <FormField
                        key={field}
                        label={t(
                          `admin_update_user_info.fields.${field}.label`
                        )}
                      >
                        <TextInput
                          value={userInfo[field]}
                          disabled={submitting}
                          onChange={(event) =>
                            setUserInfo(
                              (current) => ({
                                ...current,
                                [field]:
                                  event.target.value,
                              })
                            )
                          }
                        />
                      </FormField>
                    ))}

                    <FormField
                      label={t(
                        "admin_update_user_info.fields.reserved_notes.label",
                        "Notlar"
                      )}
                    >
                      <TextInput
                        value={
                          userInfo.reserved_notes
                        }
                        disabled={submitting}
                        onChange={(event) =>
                          setUserInfo(
                            (current) => ({
                              ...current,
                              reserved_notes:
                                event.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "admin_update_user_info.fields.role.label"
                      )}
                    >
                      <Select
                        classNamePrefix="react-select"
                        isDisabled={submitting}
                        options={[
                          "admin",
                          "driver",
                          "payer",
                        ].map((role) => ({
                          value: role,
                          label: role,
                        }))}
                        value={
                          userInfo.role
                            ? {
                                value:
                                  userInfo.role,
                                label:
                                  userInfo.role,
                              }
                            : null
                        }
                        onChange={(option) =>
                          setUserInfo(
                            (current) => ({
                              ...current,
                              role:
                                option?.value ||
                                "",
                            })
                          )
                        }
                      />
                    </FormField>

                    <Button
                      fullWidth
                      disabled={submitting}
                      onClick={submit}
                    >
                      {submitting
                        ? t(
                            "admin_update_user_info.status.submitting",
                            "Güncelleniyor..."
                          )
                        : t(
                            "admin_update_user_info.actions.submit"
                          )}
                    </Button>
                  </>
                )}
              </Stack>
            </Card>

            <StatusMessage
              message={message}
            />
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}